<template>
	<v-container class="container--narrow">
		<template v-if="loading">
			<span>Ladataan...</span>
		</template>
		<template v-else-if="error">
			<p>{{ error }}</p>
		</template>
		<template v-else>
			<v-card>
				<v-card-text v-if="form.summary">
					<p>{{ form.summary }}</p>
				</v-card-text>
				<v-card-text>
					<form @submit.prevent="submit">
						<v-select
							required
							v-model="association"
							:hint="associations.label"
							:items="associations"
							item-text="title"
							item-value="id"
							:error-messages="associationErrors"
							no-data-text="Yhdistyksiä ei saatavilla."
							label="4H-yhdistys"
							@change="$v.association.$touch()"
							@blur="$v.association.$touch()"
						>
							<template #label>
								4H-yhdistys <strong class="red--text">*</strong>
							</template>
							<v-skeleton-loader
								class="mx-auto"
								max-width="300"
								type="text"
							/>
						</v-select>
						<v-select
							required
							v-model="assignment"
							:items="assignments"
							item-text="label"
							item-value="value"
							:error-messages="assignmentErrors"
							label="Tehtävä"
							@change="$v.assignment.$touch()"
							@blur="$v.assignment.$touch()"
						>
							<template #label>
								Tehtävä <strong class="red--text">*</strong>
							</template>
						</v-select>
						<v-textarea
							:label="assignmentInformationLabel"
							:hint="assignmentInformationHint"
							rows="2"
							v-model="assignment_information"
							persistent-hint
						>
							<template #label>
								{{ assignmentInformationLabel }}
							</template>
						</v-textarea>
						<v-textarea
							required
							v-model="information"
							:error-messages="informationErrors"
							label="Lisätietoja"
							hint="Kerro miksi kiinnostuit vapaaehtoistehtävästä, millaista osaamista sinulla ja millaista osaamista tarvitsisit lisää."
							persistent-hint
							rows="2"
							@input="$v.information.$touch()"
							@blur="$v.information.$touch()"
						>
							<template #label>
								Lisätietoja <strong class="red--text">*</strong>.
							</template>
						</v-textarea>
						<v-checkbox
							required
							v-model="confirmation"
							:error-messages="confirmationErrors"
							label="Suostumus - Haluan toimia vapaaehtoisena."
							@change="$v.confirmation.$touch()"
							@blur="$v.confirmation.$touch()"
						>
							<template #label>
								Suostumus - Haluan toimia vapaaehtoisena.<strong class="red--text">*</strong>
							</template>
						</v-checkbox>
						<p>
							<small>
								4H-vapaaehtoisena sitoudun toimimaan 4H:n arvojen mukaisesti, noudattamaan 4H-yhdistyksen ohjeita ja käytäntöjä sekä käsittelemään henkilötietoja huolellisesti ja luottamuksellisesti. Annan luvan käyttää tietojani
								<v-tooltip bottom>
									<template #activator="{ on }">
										<a
											target="_blank"
											href="https://4h.fi/tietosuojaseloste/"
											@click.stop
											v-on="on"
										>
											tietosuojaselosteen
										</a>
									</template>
									Avautuu uuteen ikkunaan
								</v-tooltip>
								mukaisesti. Hyväksyn, että tarvittaessa rikostausta selvitetään 4H-yhdistyksen käytännön mukaisesti (laki Lasten kanssa toimivien rikostaustan selvittäminen).
							</small>
						</p>
						<v-btn
							type="submit"
							class="mt-4 mr-4"
							color="primary"
							:loading="formLoading"
							:disabled="formLoading"
						>
							<span>Lähetä lomake</span>
						</v-btn>
						<v-btn
							depressed
							class="mt-4"
							:loading="formLoading"
							:disabled="formLoading "
							@click="clear"
						>
							<span>Tyhjennä</span>
						</v-btn>
					</form>
				</v-card-text>
			</v-card>
		</template>
	</v-container>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, minLength } from 'vuelidate/lib/validators'

export default {
	name: 'FormItem',

	components: {
	},

	mixins: [validationMixin],

	validations: {
		association: { required },
		assignment: { required },
		information: { minLength: minLength(3) },
		confirmation: {
			checked (val) {
				return val
			},
		},
	},

	data () {
		return {
			loading: true,

			formLoading: false,
			formStatus: null,

			error: '',
			form: {},

			information: '',

			association: null,
			associations: [],

			assignment: 'yritysohjaaja',
			assignments: [
				{
					label:'Aikuisohjaaja',
					value:'aikuisohjaaja',
				},
				{
					label:'Leirinohjaaja',
					value:'leirinohjaaja',
				},
				{
					label:'Muu ohjaaja tai kouluttaja',
					value:'muu-ohjaaja-tai-kouluttaja',
				},
				{
					label: 'Yritysohjaaja',
					value: 'yritysohjaaja',
				},
				{
					label:'Muu tehtävä',
					value:'muu-tehtava',
				},
			],

			confirmation: false,
		}
	},

	computed: {
		...mapState({
			forms: state => state.formPages,
			user: state => state.user,
		}),

		// Assignment related extra information label

		assignmentInformationTexts () {
			switch (this.assignment) {
			case 'aikuisohjaaja':
				return {
					label: 'Kerro tässä kerhon tai ryhmän nimi, jota haluat ohjata, mikäli se on tiedossasi.',
					hint: 'Aikuisohjaajan tehtävänä on toimia nuorempien ohjaajien tukena ja apuna 4H-kerhoissa ja 4H-tapahtumissa. Aikuisohjaaja voi myös itse toimia ryhmänohjaajana. Aikuisohjaajana saat 4H-jäsenen vakuutuksen, tarvittavan koulutuksen tai perehdytyksen tehtävään, tuen tehtävään 4H-yhdistyksen taholta, 4H-akatemia.fi -sivuston materiaalit ja verkkokoulutukset sekä todistuksen vapaaehtoisuudesta pyydettäessä. Ohjaajana olet vakuutettu 4H-toiminnassa.',
				}
			case 'leirinohjaaja':
				return {
					label: 'Kerro millaisesta tehtävästä olet kiinnostunut tai mistä tehtävästä olet sopinut jo etukäteen.',
					hint: 'Jos tiedät leirinohjaajan nimen, kirjoita se tähän. Muussa tapauksessa voit jättää tämän tyhjäksi.',
				}
			case 'muu-ohjaaja-tai-kouluttaja':
				return {
					label: 'Kerro millaisesta muusta tehtävästä olet kiinnostunut tai mistä tehtävästä olet sopinut jo etukäteen.',
					hint: 'Jos tiedät ohjaajan tai kouluttajan nimen, kirjoita se tähän. Muussa tapauksessa voit jättää tämän tyhjäksi.',
				}
			case 'muu-tehtava':
				return {
					label: 'Kerro millaisesta muusta tehtävästä olet kiinnostunut tai mistä tehtävästä olet sopinut jo etukäteen.',
					hint: '4H-yhdistyksissä on paljon erilaisia tehtäviä, joissa voit olla vapaaehtoisena hyödyksi ja avuksi. Saat 4H-yhdistyksestä tarvittavan koulutuksen, perehdytyksen ja tuen. Voit myös hyödyntää 4H-akatemia.fi -sivuston materiaaleja ja verkkokoulutukset. Pyynnöstä saat todistuksen vapaaehtoisuudestasi. 4H-jäsenenä olet vakuutettu 4H-tapahtumissa.',
				}
			case 'yritysohjaaja':
				return {
					label: 'Kerro tässä 4H-yrittäjän nimi, jonka yritysohjaajana haluat toimia. (Täytä tämä kohta vain jos tiedät nimen.)',
					hint: 'Yritysohjaajan tehtävänä on toimia nuoren 4H-yrittäjän kannustajana. Lue tehtävästä tarkemmin 4H-akatemian sivuilta . 4H-jäsenenä olet vakuutettu 4H-tapahtumissa.',
				}
			default:
				return {
					label: 'Tehtävän lisätiedot',
					hint: 'Voit antaa lisätietoja tehtävästä tai jättää tämän tyhjäksi.',
				}
			}
		},

		assignmentInformationLabel () {
			return this.assignmentInformationTexts.label
		},
		assignmentInformationHint () {
			return this.assignmentInformationTexts.hint
		},

		// form element errors
		associationErrors () {
			const errors = []
			if (!this.$v.association.$dirty) return errors
			!this.$v.association.required && errors.push('Valitse 4H-yhdistys.')
			return errors
		},

		assignmentErrors () {
			const errors = []
			if (!this.$v.assignment.$dirty) return errors
			!this.$v.assignment.required && errors.push('Valitse tehtävä.')
			return errors
		},

		informationErrors () {
			const errors = []
			if (!this.$v.information.$dirty) return errors
			!this.$v.information.required && errors.push('Kerro miksi kiinnostuit vapaaehtoistehtävästä, millaista osaamista sinulla ja millaista osaamista tarvitsisit lisää.')
			return errors
		},

		confirmationErrors () {
			const errors = []
			if (!this.$v.confirmation.$dirty) return errors
			!this.$v.confirmation.checked && errors.push('Hyväksy ilmoittautuminen.')
			return errors
		},
	},

	mounted () {
		try {
			this.form = this.forms.find(form => {
				return form.name == this.$route.params.slug
			})

			if (!this.form) throw 'Lomaketta ei löytynyt!'

			this.$root.$emit('setHeadline', this.form.title)

			this.association = this.user._association ? {
				id: this.user._association.id,
				title: this.user._association.title,
			} : null
		} catch (error) {
			this.error = error
		}

		this.getAssociations()
	},

	methods: {
		submit () {
			this.$v.$touch()
			if (this.$v.$invalid) {
				this.formStatus = 'error'
			} else {
				this.formStatus = 'pending'
				this.postForm()
			}
		},

		clear () {
			this.$v.$reset()
			this.association = null
			this.assignment = null
			this.assignment_information = ''
			this.information = ''
			this.confirmation = false
		},

		async getAssociations () {
			try {
				var response = await this.$api.Associations.doRequest()
				this.associations = response.result.body

				// Prepend the user's _association to the associations array if it exists
				if (this.user._association) {
					const userAssociation = {
						id: this.user._association.id,
						title: this.user._association.title,
					}
					// Ensure the user's association is not duplicated
					if (!this.associations.some(assoc => assoc.id === userAssociation.id)) {
						this.associations.unshift(userAssociation)
					}
				}
			} catch (error) {
				// console.log('getAssociations() error:', error)
			}
			this.loading = false
		},

		async postForm () {
			this.formLoading = true

			try {
				await this.$api.FormItem.doRequest({
					method: 'POST',
					params: {
						slug: this.form.name,
					},
					body: {
						form_id: this.form.id,
						association: this.association.id ? this.association.id : this.association,
						assignment: [this.assignment],
						assignment_information: this.assignment_information,
						information: this.information,
						confirmation: this.confirmation,
					},
				}).on('done', (resp) => {
					if (!resp.body.error) {
						this.clear()
						this.formStatus = 'ok'
					}
				}).on('finish', () => {
					this.formLoading = false
				})
			} catch (error) {
				console.log('postForm() error:', error)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
</style>
